import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Markdown from "../components/Markdown";
import MarkdownEditor from "../components/MarkdownEditor";
import SentimentChip from "../components/SentimentChip";
import {
  ArrowLeft,
  MessageCircle,
  ArrowBigUp,
  Flag,
  Trash2,
  Send,
  Pin,
  PinOff,
  Search,
  X,
} from "lucide-react";

const PARCHMENT = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/71961a88075b238f2c3ac01d6af1c6efc499931b1c198a38074b5530f81c88c1.png";

export default function ChapterDetail() {
  const { number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [chapter, setChapter] = useState(null);
  const [verses, setVerses] = useState([]);
  const [threads, setThreads] = useState([]);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [newThread, setNewThread] = useState({ title: "", body: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("verses");
  const [searchQ, setSearchQ] = useState("");

  const load = async () => {
    try {
      const [chRes, vRes, thRes] = await Promise.all([
        api.get(`/chapters/${number}`),
        api.get(`/chapters/${number}/verses`),
        api.get(`/chapters/${number}/threads`),
      ]);
      setChapter(chRes.data);
      setVerses(vRes.data);
      setThreads(thRes.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [number]);

  const reloadThreads = async (q) => {
    try {
      const url = q && q.trim()
        ? `/threads/search?q=${encodeURIComponent(q)}&chapter=${number}`
        : `/chapters/${number}/threads`;
      const { data } = await api.get(url);
      setThreads(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  const submitThread = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/chapters/${number}/threads`, newThread);
      setNewThread({ title: "", body: "" });
      setShowForm(false);
      await reloadThreads(searchQ);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVote = async (tid) => {
    if (!user || user === false) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/vote`, { target_type: "thread", target_id: tid });
      setThreads((list) =>
        list.map((t) => (t.id === tid ? { ...t, upvotes: data.count, has_voted: data.voted } : t))
      );
    } catch (e) { setError(formatApiError(e)); }
  };

  const report = async (tid) => {
    if (!user || user === false) { navigate("/login"); return; }
    const reason = window.prompt("Why are you reporting this thread? (at least 3 characters)");
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/reports`, { target_type: "thread", target_id: tid, reason: reason.trim() });
      alert("Reported. A moderator will review it soon.");
    } catch (e) { alert(formatApiError(e)); }
  };

  const remove = async (tid) => {
    if (!window.confirm("Delete this thread?")) return;
    try {
      await api.delete(`/threads/${tid}`);
      await reloadThreads(searchQ);
    } catch (e) { alert(formatApiError(e)); }
  };

  const togglePin = async (t) => {
    try {
      await api.post(`/threads/${t.id}/pin`, { pinned: !t.pinned });
      await reloadThreads(searchQ);
    } catch (e) { alert(formatApiError(e)); }
  };

  if (!chapter) {
    return (
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">
        <div className="h-8 w-48 bg-[#F3EFE9] rounded animate-pulse" />
        <div className="mt-6 h-12 w-2/3 bg-[#F3EFE9] rounded animate-pulse" />
        <div className="mt-4 h-4 w-1/3 bg-[#F3EFE9] rounded animate-pulse" />
        {error && <div className="mt-6 text-sm text-[#C05B3B]">{error}</div>}
      </div>
    );
  }

  return (
    <div data-testid="chapter-detail-page">
      {/* Header */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-10 lg:pt-14">
        <Link to="/chapters" className="inline-flex items-center gap-2 text-sm text-[#5C6B5F] hover:text-[#D96C4A]" data-testid="back-to-chapters">
          <ArrowLeft size={16} /> All chapters
        </Link>
        <div className="mt-6 flex items-start gap-6 animate-fade-up">
          <div className="chapter-ornament">{chapter.number.toString().padStart(2, "0")}</div>
          <div className="flex-1">
            <div className="text-xs tracking-widest uppercase text-[#8A948C]">
              Adhyāya {chapter.number} · {chapter.verses_count} verses
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2D3B31] mt-2 leading-tight">
              {chapter.title}
            </h1>
            <div className="font-serif-display italic text-xl text-[#8A948C] mt-1">
              {chapter.subtitle}
            </div>
          </div>
        </div>

        <p className="mt-10 text-lg leading-relaxed text-[#2D3B31] font-light max-w-3xl animate-fade-up animate-delay-1">
          {chapter.summary}
        </p>
      </section>

      {/* Tabs */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 mt-14">
        <div className="flex gap-2 border-b" style={{ borderColor: "#EAE5D9" }}>
          <TabBtn active={tab === "verses"} onClick={() => setTab("verses")} testId="chapter-tab-verses">
            Verses ({verses.length})
          </TabBtn>
          <TabBtn active={tab === "discussion"} onClick={() => setTab("discussion")} testId="chapter-tab-discussion">
            Discussion ({threads.filter((t) => !t.deleted).length})
          </TabBtn>
        </div>
      </section>

      {/* VERSES TAB */}
      {tab === "verses" && (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 mt-10 pb-16" data-testid="verses-section">
          {verses.length === 0 ? (
            <div className="organic-card p-10 text-center">
              <p className="text-[#5C6B5F]">No verses available yet for this chapter.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {verses.map((v) => (
                <div key={v.reference} className="verse-card animate-fade-up" data-testid={`verse-${v.reference}`}>
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div className="text-xs tracking-widest uppercase text-[#D96C4A]">
                      Gītā {v.reference}
                    </div>
                  </div>
                  <p className="shloka mt-3 text-2xl text-[#2D3B31]">{v.sanskrit}</p>
                  <p className="mt-2 text-sm italic text-[#8A948C] font-serif-display">{v.transliteration}</p>
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: "#EAE5D9" }}>
                    <p className="text-base text-[#2D3B31] italic font-serif-display leading-relaxed">
                      “{v.translation}”
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-10 rounded-2xl p-6 text-center" style={{ background: "#F3EFE9" }}>
            <p className="text-sm text-[#5C6B5F]">
              Wish to discuss what you've read?{" "}
              <button className="text-[#D96C4A] hover:underline" onClick={() => setTab("discussion")} data-testid="goto-discussion">
                Open discussion →
              </button>
            </p>
          </div>
        </section>
      )}

      {/* DISCUSSION TAB */}
      {tab === "discussion" && (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 mt-10 pb-16" data-testid="discussion-section">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs tracking-widest uppercase text-[#D96C4A]">The conversation</div>
              <h2 className="font-serif-display text-3xl sm:text-4xl font-medium text-[#2D3B31] mt-2">
                Reflections from the sabhā
              </h2>
            </div>
            {user && user !== false ? (
              <button
                onClick={() => setShowForm(!showForm)}
                className="btn-accent inline-flex items-center gap-2"
                data-testid="open-new-thread-form"
              >
                <MessageCircle size={18} />
                {showForm ? "Close" : "Start a thread"}
              </button>
            ) : (
              <Link to="/login" className="btn-accent" data-testid="login-to-discuss">
                Sign in to discuss
              </Link>
            )}
          </div>

          {/* Search bar */}
          <div className="mt-6 relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A948C] z-10" />
            <input
              type="text"
              className="input-organic pr-10"
              style={{ paddingLeft: "2.75rem" }}
              placeholder="Search threads in this chapter…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") reloadThreads(searchQ); }}
              data-testid="thread-search-input"
            />
            {searchQ && (
              <button
                onClick={() => { setSearchQ(""); reloadThreads(""); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A948C] hover:text-[#D96C4A]"
                data-testid="thread-search-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {showForm && user && user !== false && (
            <form onSubmit={submitThread} className="organic-card p-8 mt-6" data-testid="new-thread-form">
              <label className="text-xs uppercase tracking-widest text-[#8A948C]">Title</label>
              <input
                className="input-organic mt-2"
                placeholder="A question, a reflection, a shloka that moved you..."
                value={newThread.title}
                maxLength={200}
                minLength={3}
                required
                onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                data-testid="new-thread-title"
              />
              <div className="mt-5">
                <label className="text-xs uppercase tracking-widest text-[#8A948C] block mb-2">Your thoughts</label>
                <MarkdownEditor
                  value={newThread.body}
                  onChange={(v) => setNewThread({ ...newThread, body: v })}
                  placeholder="Write with care. Markdown formatting is supported."
                  testIdPrefix="new-thread-body"
                />
              </div>
              <div className="mt-5 flex justify-end gap-3">
                <button type="button" className="btn-outline" onClick={() => setShowForm(false)} data-testid="cancel-new-thread">
                  Cancel
                </button>
                <button type="submit" className="btn-accent inline-flex items-center gap-2" disabled={submitting} data-testid="submit-new-thread">
                  <Send size={16} />
                  {submitting ? "Posting..." : "Post thread"}
                </button>
              </div>
            </form>
          )}

          {error && (
            <div className="mt-6 text-sm text-[#C05B3B] bg-[#F2D8CF]/50 border border-[#F2D8CF] rounded-lg px-4 py-2" data-testid="chapter-error">
              {error}
            </div>
          )}

          <div className="mt-8 space-y-4" data-testid="threads-list">
            {threads.length === 0 ? (
              <div className="organic-card p-10 text-center">
                <div className="font-serif-display text-2xl text-[#2D3B31]">
                  {searchQ ? "No matches" : "No threads yet"}
                </div>
                <p className="text-[#5C6B5F] mt-2 text-sm">
                  {searchQ ? "Try a different search term." : "Be the first to share a reflection on this chapter."}
                </p>
              </div>
            ) : (
              threads.map((t) => (
                <div key={t.id} className="organic-card p-6 md:p-8" data-testid={`thread-item-${t.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {t.pinned && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#C05B3B] bg-[#F2D8CF] px-2 py-0.5 rounded-full" data-testid={`thread-pinned-${t.id}`}>
                            <Pin size={10} /> Pinned
                          </span>
                        )}
                        {t.sentiment && <SentimentChip sentiment={t.sentiment} size="xs" />}
                      </div>
                      <Link to={`/threads/${t.id}`} className="font-serif-display text-xl md:text-2xl text-[#2D3B31] hover:text-[#D96C4A] transition-colors block">
                        {t.title}
                      </Link>
                      <div className="text-xs text-[#8A948C] mt-1">
                        by <span className="text-[#5C6B5F]">{t.author?.name}</span>
                        {" · "}
                        {new Date(t.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                      </div>
                      <div className="mt-3 text-[#5C6B5F] line-clamp-3">
                        <Markdown>{t.body}</Markdown>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleVote(t.id)}
                      className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl border transition-colors ${
                        t.has_voted
                          ? "bg-[#F2D8CF] border-[#D96C4A] text-[#C05B3B]"
                          : "border-[#EAE5D9] text-[#5C6B5F] hover:border-[#D96C4A] hover:text-[#D96C4A]"
                      }`}
                      data-testid={`thread-upvote-${t.id}`}
                    >
                      <ArrowBigUp size={18} fill={t.has_voted ? "#C05B3B" : "none"} />
                      <span className="text-xs font-medium">{t.upvotes}</span>
                    </button>
                  </div>

                  <div className="mt-5 pt-4 border-t flex items-center justify-between flex-wrap gap-2" style={{ borderColor: "#EAE5D9" }}>
                    <Link to={`/threads/${t.id}`} className="text-sm text-[#5C6B5F] hover:text-[#D96C4A] inline-flex items-center gap-1.5" data-testid={`thread-open-${t.id}`}>
                      <MessageCircle size={16} /> {t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}
                    </Link>
                    <div className="flex items-center gap-3 text-xs">
                      {user && user !== false && user.role === "admin" && (
                        <button
                          onClick={() => togglePin(t)}
                          className="inline-flex items-center gap-1 text-[#8A948C] hover:text-[#D96C4A]"
                          data-testid={`thread-pin-${t.id}`}
                        >
                          {t.pinned ? <><PinOff size={14} /> Unpin</> : <><Pin size={14} /> Pin</>}
                        </button>
                      )}
                      {user && user !== false && (
                        <button onClick={() => report(t.id)} className="inline-flex items-center gap-1 text-[#8A948C] hover:text-[#D96C4A]" data-testid={`thread-report-${t.id}`}>
                          <Flag size={14} /> Report
                        </button>
                      )}
                      {user && user !== false && (user.role === "admin" || user.id === t.author?.id) && (
                        <button onClick={() => remove(t.id)} className="inline-flex items-center gap-1 text-[#8A948C] hover:text-[#C05B3B]" data-testid={`thread-delete-${t.id}`}>
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
        active ? "border-[#D96C4A] text-[#D96C4A]" : "border-transparent text-[#5C6B5F] hover:text-[#2D3B31]"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
